import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { cn } from 'components/lib/utils';

interface IDayCardProps {
  dayLabel: string;
  rewardCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

interface Position {
  x: number;
  y: number;
}

const PANEL_WIDTH = 320;
const PANEL_OFFSET = 8;

export const DayCard: FC<IDayCardProps> = ({
  dayLabel,
  rewardCount,
  isOpen,
  onOpenChange,
  onClose,
  children,
  footer
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClose = useCallback(() => {
    setPosition(null);
    onClose?.();
    onOpenChange(false);
  }, [onClose, onOpenChange]);

  const computeInitialPosition = useCallback((): Position => {
    if (!triggerRef.current) return { x: 0, y: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const PANEL_MAX_HEIGHT = 494;
    const spaceBelow = viewportHeight - rect.bottom - PANEL_OFFSET;

    let x = rect.left;
    let y = spaceBelow >= Math.min(PANEL_MAX_HEIGHT / 2, 200)
      ? rect.bottom + PANEL_OFFSET
      : Math.max(0, rect.top - PANEL_MAX_HEIGHT - PANEL_OFFSET);

    if (x + PANEL_WIDTH > viewportWidth) {
      x = viewportWidth - PANEL_WIDTH - 8;
    }

    return { x: Math.max(0, x), y: Math.max(0, y) };
  }, []);

  useEffect(() => {
    if (isOpen && position === null) {
      let rafId = requestAnimationFrame(() => setPosition(computeInitialPosition()));
      return () => cancelAnimationFrame(rafId);
    }
    if (!isOpen) {
      setPosition(null);
    }

    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, input, select, [role="combobox"], [data-radix-popper-content-wrapper], [data-day-card-scroll]')) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: position?.x ?? 0,
      originY: position?.y ?? 0
    };
    setIsDragging(true);
  }, [position]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const maxX = window.innerWidth - PANEL_WIDTH;
    const maxY = window.innerHeight - panelHeight;
    setPosition({
      x: Math.min(Math.max(0, dragState.current.originX + dx), maxX),
      y: Math.min(Math.max(0, dragState.current.originY + dy), maxY)
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragState.current = null;
    setIsDragging(false);
  }, []);

  const panel =
    isOpen && position != null
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-modal={false}
            style={{
              position: 'fixed',
              left: position.x,
              top: position.y,
              width: PANEL_WIDTH,
              zIndex: 9999,
              cursor: isDragging ? 'grabbing' : undefined,
              userSelect: isDragging ? 'none' : undefined
            }}
            className={cn(
              'relative rounded-md border border-base-border bg-base-background p-6 shadow-xl',
              'flex min-h-0 max-h-[min(494px,calc(100dvh-5rem))] flex-col overflow-hidden',
              'animate-in fade-in-0 zoom-in-95 duration-100'
            )}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div
              className="absolute inset-x-0 top-0 h-8 cursor-grab active:cursor-grabbing"
              aria-hidden
            />

            <button
              type="button"
              aria-label="Close day rewards"
              className="absolute right-4 top-4 z-20 inline-flex size-6 items-center justify-center rounded text-black hover:opacity-80"
              onClick={handleClose}
            >
              <FontAwesomeIcon
                icon="fa-regular fa-xmark"
                className="text-p-s font-semibold leading-none text-black"
              />
            </button>

            <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
              {children}
              {footer != null ? <div className="mt-2 shrink-0">{footer}</div> : null}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          onOpenChange(!isOpen);
        }}
        className={cn(
          'group flex h-8 w-32 min-w-32 shrink-0 items-center justify-between rounded-md border border-base-input bg-base-background py-2 px-3 text-p-s transition-colors',
          'hover:border-indigo-600 hover:bg-purple-50',
          isOpen && 'border-indigo-600 bg-purple-50'
        )}
      >
        <span
          className={cn(
            'text-p-xs font-medium leading-none text-base-foreground',
            !isOpen && 'group-hover:text-indigo-600'
          )}
        >
          {dayLabel}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1',
            rewardCount > 0 ? 'text-indigo-500' : 'text-base-mutedForeground'
          )}
        >
          <FontAwesomeIcon
            icon="fa-regular fa-gift"
            className="text-sm leading-none"
          />
          <span>{rewardCount}</span>
        </span>
      </button>

      {panel}
    </>
  );
};
